import { useEffect, useState } from 'react'
import api from '../api/axios'
import {
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ShieldExclamationIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface Company {
  id: string
  company_name: string
  company_code: string
  database_name: string
  admin_url_slug?: string
}

interface Template {
  filename: string
  size: number
  isDefault: boolean
}

interface Props {
  company: Company
  onClose: () => void
  onCompleted?: () => void
}

type Tool = 'slug' | 'rename' | 'clean'

export default function ReprovisionModal({ company, onClose, onCompleted }: Props) {
  const [tool, setTool] = useState<Tool>('slug')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  // Slug
  const [newSlug, setNewSlug] = useState(company.admin_url_slug || '')
  const [slugConfirm, setSlugConfirm] = useState('')

  // Rename
  const [newName, setNewName] = useState(company.database_name)
  const [renameConfirm, setRenameConfirm] = useState('')

  // Clean
  const [templates, setTemplates] = useState<Template[]>([])
  const [cleanTemplate, setCleanTemplate] = useState('')
  const [cleanConfirm, setCleanConfirm] = useState('')

  useEffect(() => {
    if (tool !== 'clean') return
    api.get('/super-admin/schema-templates')
      .then(({ data }) => {
        setTemplates(data.templates || [])
        const def = (data.templates || []).find((t: Template) => t.isDefault) || (data.templates || [])[0]
        if (def) setCleanTemplate(def.filename)
      })
      .catch(() => setTemplates([]))
  }, [tool])

  const RENAME_PHRASE = `RENAME ${company.database_name}`
  const CLEAN_PHRASE = `CLEAN ${company.database_name}`

  const runChangeSlug = async () => {
    setError(''); setSuccessMsg('')
    if (!/^[a-z0-9-]+$/.test(newSlug)) {
      setError('Slug must contain only lowercase letters, numbers, and hyphens')
      return
    }
    setBusy(true)
    try {
      const { data } = await api.post(`/super-admin/companies/${company.id}/change-slug`, {
        newSlug,
        confirm: true,
      })
      setSuccessMsg(`Slug changed: ${data.oldSlug || '—'} → ${data.newSlug}`)
      onCompleted?.()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to change slug')
    } finally {
      setBusy(false)
    }
  }

  const runRenameDatabase = async () => {
    setError(''); setSuccessMsg('')
    if (!/^[A-Za-z0-9_-]+$/.test(newName)) {
      setError('Database name may contain only letters, numbers, underscores, and hyphens')
      return
    }
    if (newName === company.database_name) {
      setError('New name is identical to current name')
      return
    }
    if (renameConfirm !== RENAME_PHRASE) {
      setError(`Type "${RENAME_PHRASE}" to confirm`)
      return
    }
    setBusy(true)
    try {
      const { data } = await api.post(`/super-admin/companies/${company.id}/rename-database`, {
        newName,
        confirm: true,
      })
      setSuccessMsg(`Renamed ${data.oldName} → ${data.newName} (${data.movedTables} tables moved)`)
      onCompleted?.()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to rename database')
    } finally {
      setBusy(false)
    }
  }

  const runCleanDatabase = async () => {
    setError(''); setSuccessMsg('')
    if (cleanConfirm !== CLEAN_PHRASE) {
      setError(`Type "${CLEAN_PHRASE}" to confirm`)
      return
    }
    setBusy(true)
    try {
      const { data } = await api.post(`/super-admin/companies/${company.id}/clean-database`, {
        templateName: cleanTemplate || undefined,
        confirm: true,
      })
      const importRes = data.importResult || {}
      setSuccessMsg(
        `Dropped ${data.droppedTables} tables. Re-imported ${data.templateUsed} → ${importRes.tablesCreated || 0} tables created${
          importRes.errorsEncountered?.length ? ` (with ${importRes.errorsEncountered.length} warnings)` : ''
        }.`
      )
      onCompleted?.()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to clean database')
    } finally {
      setBusy(false)
    }
  }

  const tabs: { id: Tool; label: string; icon: any; tone: string }[] = [
    { id: 'slug', label: 'Change slug', icon: LinkIcon, tone: 'blue' },
    { id: 'rename', label: 'Rename DB', icon: ArrowsRightLeftIcon, tone: 'amber' },
    { id: 'clean', label: 'Clean DB', icon: TrashIcon, tone: 'red' },
  ]

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl my-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ArrowPathIcon className="w-5 h-5 text-slate-500" />
              Re-provision — {company.company_name}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">
              {company.company_code} · {company.database_name}
            </p>
          </div>
          <button onClick={() => !busy && onClose()} className="p-1 rounded hover:bg-slate-100" disabled={busy}>
            <XMarkIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="border-b border-slate-200">
          <nav className="flex px-4">
            {tabs.map(t => {
              const Icon = t.icon
              const active = tool === t.id
              return (
                <button
                  key={t.id}
                  onClick={() => { setTool(t.id); setError(''); setSuccessMsg('') }}
                  disabled={busy}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    active
                      ? `border-${t.tone}-500 text-${t.tone}-700`
                      : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <ShieldExclamationIcon className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-pre-wrap">{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-start gap-2">
              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-pre-wrap">{successMsg}</span>
            </div>
          )}

          {tool === 'slug' && (
            <>
              <p className="text-sm text-slate-600">
                Updates <span className="font-mono">admin_url_slug</span> in the registry. The admin-portal URL for this
                company becomes <span className="font-mono">https://{newSlug || 'your-slug'}.cmapps.dev</span>.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New slug</label>
                <input
                  value={newSlug}
                  onChange={e => setNewSlug(e.target.value.toLowerCase())}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                  placeholder="acme"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Lowercase letters, numbers, and hyphens only. Current: <span className="font-mono">{company.admin_url_slug || '(none)'}</span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Type new slug again to confirm</label>
                <input
                  value={slugConfirm}
                  onChange={e => setSlugConfirm(e.target.value.toLowerCase())}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                />
              </div>
              <div className="flex justify-end">
                <button
                  disabled={busy || !newSlug || slugConfirm !== newSlug}
                  onClick={runChangeSlug}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm disabled:opacity-50"
                >
                  {busy ? 'Updating…' : 'Change slug'}
                </button>
              </div>
            </>
          )}

          {tool === 'rename' && (
            <>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Destructive — moves every base table to a new DB, then drops the old one.</div>
                  Any open connection pool against <span className="font-mono">{company.database_name}</span> will error until the
                  server picks up the new name. Best done during low traffic.
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New database name</label>
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Current: <span className="font-mono">{company.database_name}</span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Type <span className="font-mono bg-slate-100 px-1 rounded">{RENAME_PHRASE}</span> to confirm
                </label>
                <input
                  value={renameConfirm}
                  onChange={e => setRenameConfirm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                />
              </div>
              <div className="flex justify-end">
                <button
                  disabled={busy || renameConfirm !== RENAME_PHRASE || newName === company.database_name}
                  onClick={runRenameDatabase}
                  className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 shadow-sm disabled:opacity-50"
                >
                  {busy ? 'Renaming…' : 'Rename database'}
                </button>
              </div>
            </>
          )}

          {tool === 'clean' && (
            <>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800 flex items-start gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Extreme — drops every table in <span className="font-mono">{company.database_name}</span>.</div>
                  All tenant data is lost and replaced with the schema template + seed rows. The company record, credentials,
                  and DB name stay the same.
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Re-import template</label>
                <select
                  value={cleanTemplate}
                  onChange={e => setCleanTemplate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                >
                  {templates.length === 0 && <option value="">(no templates found)</option>}
                  {templates.map(t => (
                    <option key={t.filename} value={t.filename}>
                      {t.filename} {t.isDefault ? '(default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Type <span className="font-mono bg-slate-100 px-1 rounded">{CLEAN_PHRASE}</span> to confirm
                </label>
                <input
                  value={cleanConfirm}
                  onChange={e => setCleanConfirm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                />
              </div>
              <div className="flex justify-end">
                <button
                  disabled={busy || cleanConfirm !== CLEAN_PHRASE || !cleanTemplate}
                  onClick={runCleanDatabase}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm disabled:opacity-50"
                >
                  {busy ? 'Cleaning…' : 'Wipe & re-import'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
